/**
 * Shared utility for table layout calculations
 */

interface TableSizeConfig {
  columnCount: number;
  columnSizes?: string[];
}

/**
 * Calculate adaptive minimum width for a column based on count and size
 */
export const getAdaptiveMinWidth = (
  columnSize: string,
  columnCount: number
): number => {
  // Sanitize columnCount to prevent RangeError and handle edge cases
  const safeColumnCount = Math.max(
    0,
    Math.min(100, Math.floor(Number(columnCount) || 0))
  );

  // Base widths - generous defaults for readability
  const baseWidths = {
    small: 180,
    medium: 240,
    large: 350,
  };

  const baseWidth =
    baseWidths[columnSize as keyof typeof baseWidths] || baseWidths.medium;

  // Only reduce width for tables with many columns
  if (safeColumnCount <= 4) {
    return baseWidth; // Keep full size for smaller tables
  } else if (safeColumnCount <= 7) {
    return Math.max(baseWidth * 0.85, 120); // Gentle reduction
  } else if (safeColumnCount <= 12) {
    return Math.max(baseWidth * 0.7, 100); // Moderate reduction
  } else {
    // For very wide tables, more aggressive reduction
    return Math.max(baseWidth * 0.5, 80);
  }
};

/**
 * Calculate estimated table width with smart sizing
 */
export const calculateTableWidth = ({
  columnCount,
  columnSizes = [],
}: TableSizeConfig): number => {
  // Sanitize columnCount to prevent RangeError and handle edge cases
  const safeColumnCount = Math.max(
    0,
    Math.min(100, Math.floor(Number(columnCount) || 0))
  );

  if (safeColumnCount === 0) return 600;

  // Calculate total minimum width
  const totalMinWidth = Array.from({ length: safeColumnCount }, (_, i) => {
    const columnSize = columnSizes[i] || 'medium';
    return getAdaptiveMinWidth(columnSize, safeColumnCount);
  }).reduce((sum, width) => sum + width, 0);

  // Smart width calculation
  const maxChatWidth = 1200; // Max width for larger tables
  const padding = 80; // Padding for better spacing
  const calculatedWidth = totalMinWidth + padding;

  // Allow tables to be properly sized, only constrain when necessary
  if (safeColumnCount <= 3) {
    // Small tables: use calculated width, minimum 600px
    return Math.max(calculatedWidth, 600);
  } else if (safeColumnCount <= 6) {
    // Medium tables: allow wider but cap at max chat width
    return Math.min(calculatedWidth, maxChatWidth);
  } else {
    // Large tables: try to fit reasonably, allow overflow if needed
    return Math.max(Math.min(calculatedWidth, maxChatWidth), 800);
  }
};
